import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { PlusOutlined, SearchOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Row, Col, Space, Button, Divider, Spin, Empty, Pagination, Select, Input } from 'antd';

const ListView = forwardRef(
    (
        {
            title,
            description,
            fetchFn,
            defaultPageSize = 10,
            pageSizeOptions = ['10', '20', '50', '100'],
            orderingFields = [],
            showSearch = true,
            onCreate,
            createButtonText,
            headerExtra,
            renderItem,
            emptyState,
            gutter = [16, 16],
            className,
            grid = { xs: 24, sm: 24, md: 12, lg: 8, xl: 8 },
        },
        ref,
    ) => {
        const [page, setPage] = useState(1);
        const [pageSize, setPageSize] = useState(defaultPageSize);
        const [search, setSearch] = useState('');
        const [ordering, setOrdering] = useState('');

        const [items, setItems] = useState([]);
        const [totalCount, setTotalCount] = useState(0);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null);

        const loadData = useCallback(() => {
            setLoading(true);
            setError(null);
            Promise.resolve(fetchFn({ page, page_size: pageSize, search, ordering }))
                .then((data) => {
                    setItems(data?.results ?? []);
                    setTotalCount(data?.count ?? 0);
                })
                .catch((err) => setError(err))
                .finally(() => setLoading(false));
        }, [fetchFn, page, pageSize, search, ordering]);

        useEffect(() => {
            loadData();
        }, [loadData]);

        useImperativeHandle(ref, () => ({ refetch: loadData }), [loadData]);

        const handlePageChange = (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
        };

        const handleSearch = (value) => {
            setSearch(value || '');
            setPage(1);
        };

        const handleOrdering = (value) => {
            setOrdering(value || '');
            setPage(1);
        };

        const itemRender = (_page, type, originalElement) => {
            if (type === 'prev') return <Button type='default' icon={<LeftOutlined />} />;
            if (type === 'next') return <Button type='default' icon={<RightOutlined />} />;
            return originalElement;
        };

        return (
            <div className={className} style={{ height: '100%', width: '100%' }}>
                {/* Header */}
                <Row justify='space-between' align='middle' gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <Space direction='vertical' size={0}>
                            {title && (
                                <div style={{ fontWeight: 'bold', fontSize: '30px' }}>{title}</div>
                            )}
                            {description && (
                                <div style={{ color: '#666', fontSize: '14px' }}>{description}</div>
                            )}
                        </Space>
                    </Col>

                    <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                        <Row justify='end' align='middle' gutter={[8, 8]}>
                            {headerExtra}

                            {totalCount > 0 && (
                                <Col>
                                    <Pagination
                                        pageSizeOptions={pageSizeOptions}
                                        current={page}
                                        total={totalCount}
                                        onChange={handlePageChange}
                                        pageSize={pageSize}
                                        showSizeChanger
                                        responsive={true}
                                        locale={{ items_per_page: '/ page' }}
                                        itemRender={itemRender}
                                    />
                                </Col>
                            )}

                            {orderingFields.length > 0 && (
                                <Col>
                                    <Select
                                        placeholder='Order By'
                                        style={{ width: 140 }}
                                        value={ordering || undefined}
                                        onChange={handleOrdering}
                                        allowClear
                                    >
                                        {orderingFields.map((field) => (
                                            <Select.Option key={field.id} value={field.id}>
                                                {field.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            )}

                            {showSearch && (
                                <Col>
                                    <Input
                                        placeholder='Search...'
                                        suffix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                        style={{ width: 200 }}
                                        value={search}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        allowClear
                                    />
                                </Col>
                            )}

                            {onCreate && (
                                <Col>
                                    <Button
                                        shape='circle'
                                        type='primary'
                                        icon={<PlusOutlined />}
                                        onClick={onCreate}
                                        title={createButtonText}
                                        style={{ background: '#377EF3' }}
                                    />
                                </Col>
                            )}
                        </Row>
                    </Col>
                </Row>

                <Divider />

                {/* Content */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Spin size='large' />
                    </div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '100px 0', color: 'red' }}>
                        <h3>Error loading data</h3>
                        <p>{error?.message || 'Something went wrong'}</p>
                        <Button onClick={loadData}>Try Again</Button>
                    </div>
                ) : items.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '100px 0' }}>
                        <Empty
                            image={emptyState?.icon}
                            description={
                                <Space direction='vertical' size={0}>
                                    <div style={{ fontWeight: 'bold' }}>
                                        {emptyState?.title || 'No results found'}
                                    </div>
                                    <div>
                                        {emptyState?.description ||
                                            'There are no items to display at this time.'}
                                    </div>
                                </Space>
                            }
                        />
                    </div>
                ) : (
                    <Row gutter={gutter} style={{ marginTop: '10px' }}>
                        {items.map((item, index) => (
                            <Col key={item.id ?? index} {...grid}>
                                {renderItem({ item, index })}
                            </Col>
                        ))}
                    </Row>
                )}
            </div>
        );
    },
);

ListView.displayName = 'ListView';

ListView.propTypes = {
    title: PropTypes.node,
    description: PropTypes.string,
    fetchFn: PropTypes.func.isRequired,
    defaultPageSize: PropTypes.number,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.string),
    orderingFields: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
    ),
    showSearch: PropTypes.bool,
    onCreate: PropTypes.func,
    createButtonText: PropTypes.string,
    headerExtra: PropTypes.node,
    renderItem: PropTypes.func.isRequired,
    emptyState: PropTypes.shape({
        icon: PropTypes.node,
        title: PropTypes.string,
        description: PropTypes.string,
    }),
    gutter: PropTypes.arrayOf(PropTypes.number),
    className: PropTypes.string,
    grid: PropTypes.object,
};

export default ListView;
